// Minimal SaveService (IMPLEMENTATION §2.9 "Save", §3 M5): load / save / reset on localStorage.
//   keys      gunmerger.save (main) · gunmerger.save.bak (the previous good main) · gunmerger.save.corrupt
//             (the last main that failed to load, kept for forensics; never read back)
//   envelope  {v, game, savedAtWallMs, lastSeenWallMs, maxWallSeenMs, defsHash, sim, meta}
//   load      JSON → NaN / ±Infinity sanitized (M11, save/sanitize.ts) → version-agnostic header (v, game,
//             sim.v) → migrations on the raw object → nulls where the current format has numbers → zod
//             envelope (the current format) → integers only → strip transient (held, hold, lastSale, undo,
//             tapCd, tapBuf) → on a defsHash mismatch sim/reconcile.ts drops what the current defs do not know
//             (with a refund, and a telemetry row) → checkInvariants ALWAYS. A main that fails is kept as
//             .corrupt and the .bak is tried; neither → the caller starts fresh. A load that passes after a
//             repair logs one `save_sanitized` row.
//   save      the new envelope becomes the main (non-finite numbers written as 0 + a `save_sanitized` row);
//             the previous main moves to .bak only once the new main is written, only when the sim moved on
//             and only when it was a good save. `disable()` (M11: this instance lost the single-instance
//             lock) refuses every later write. Compare-and-swap (M11 review fix): a save is refused as a
//             `conflict` when the main under the key is no longer the one this service last loaded, adopted
//             or wrote — another instance wrote it meanwhile, so this one is stale (game/lifecycle.ts then
//             treats it like a lost lock) and its state never overwrites the newer save.
//   reset     removes main and .bak.
// Topology / occupancy are derived by the sim on every command and tick, so nothing is rebuilt here.
import { canonicalJson, hashValue } from '../sim/core/hash.ts';
import type { SimDefs } from '../sim/defs.ts';
import { checkInvariants } from '../sim/invariants.ts';
import { type ReconcileReport, reconcileWithDefs } from '../sim/reconcile.ts';
import { coldOpenState } from '../sim/setup.ts';
import type { SimState } from '../sim/state.ts';
import {
  MIGRATIONS,
  type Migration,
  migrate,
  type RawEnvelope,
  SAVE_VERSION,
} from './migrations.ts';
import {
  describeFixes,
  jsonCopyKeepingNonFinite,
  repairNulls,
  type SanitizeFix,
  sanitizeNonFinite,
  stringifySanitized,
} from './sanitize.ts';
import { checkEnvelope, checkHeader, SAVE_GAME, type SaveMeta } from './schema.ts';
import type { KeyValueStore } from './storage.ts';

export const SAVE_KEYS = {
  main: 'gunmerger.save',
  bak: 'gunmerger.save.bak',
  corrupt: 'gunmerger.save.corrupt',
} as const;

export type { SaveMeta } from './schema.ts';

export interface SaveEnvelope {
  v: number;
  game: typeof SAVE_GAME;
  savedAtWallMs: number;
  lastSeenWallMs: number;
  maxWallSeenMs: number;
  defsHash: string;
  sim: SimState;
  meta: SaveMeta;
}

export interface Loaded {
  state: SimState;
  meta: SaveMeta;
  env: SaveEnvelope;
  /** Non-null when the save was written with other defs (what was dropped and refunded). */
  mismatch: ReconcileReport | null;
}

export type LoadResult =
  | { k: 'none' }
  | ({ k: 'loaded'; from: 'main' | 'bak'; corrupt: string | null } & Loaded)
  | { k: 'failed'; reason: string };

export interface TelemetryRow {
  event: string;
  subject: string;
  value: number;
  extra: string;
}

export interface SaveServiceOptions {
  defs: SimDefs;
  /** hashValue(defs), computed once at boot. */
  defsHash: string;
  /** Wall clock (Date.now in the app). */
  now(): number;
  log(row: TelemetryRow): void;
  /** Save-format migrations (default: MIGRATIONS; tests inject a table). */
  migrations?: Readonly<Record<number, Migration>>;
  /** Repaired paths listed in a `save_sanitized` row (ui.json save.sanitizeLogMax; default: all). */
  sanitizeLogMax?: number;
}

export interface SaveWrite {
  ok: boolean;
  bytes: number;
  /** True when nothing was written because the service is disabled (single-instance lock lost). */
  blocked?: boolean;
  /** True when nothing was written because another instance wrote the main since (compare-and-swap). */
  conflict?: boolean;
}

/** The transient fields a save never carries into a session (§0.1, R13 "the stack is cleared on load"). */
export function stripTransient(s: SimState): void {
  const t = s.transient;
  t.held = null;
  t.hold = null;
  t.lastSale = null;
  t.undo = [];
  t.tapCd = 0;
  t.tapBuf = [];
}

export function defsHashOf(d: SimDefs): string {
  return hashValue(d);
}

export class SaveService {
  private readonly store: KeyValueStore;
  private o: SaveServiceOptions;
  /**
   * The main text we last loaded or wrote (known good). Anything else found under the main key (another
   * tab, a hand edit) is validated before it may become the .bak.
   */
  private mainText: string | null = null;
  /**
   * The sim JSON under the main key (as the game continues from it). A save whose sim is unchanged (the
   * hidden + pagehide pair, a quiet autosave) rewrites the main's timestamps but does not rotate it into
   * .bak, so .bak stays the previous DISTINCT good save.
   */
  private mainSim: string | null = null;
  /**
   * The text under the main key when this service last loaded, adopted or wrote it (also a corrupt main
   * that fell back to .bak) — the compare-and-swap baseline. undefined: never looked (the first save adopts).
   */
  private seen: string | null | undefined = undefined;
  private maxWall = 0;
  private disabled = false;
  private template: { defs: SimDefs; env: SaveEnvelope } | null = null;

  constructor(store: KeyValueStore, o: SaveServiceOptions) {
    this.store = store;
    this.o = o;
  }

  /** New defs (DEV data hot reload): later loads reconcile against them and saves carry their hash. */
  setDefs(defs: SimDefs, defsHash: string): void {
    this.o = { ...this.o, defs, defsHash };
  }

  /** From now on every save / reset is refused (this instance lost the single-instance lock, M11). */
  disable(): void {
    this.disabled = true;
  }

  get writable(): boolean {
    return !this.disabled;
  }

  /**
   * True when the main under the save key is not the one this service last loaded, adopted or wrote
   * (another instance saved since: a stale page must reload or stop instead of overwriting it).
   */
  mainChanged(): boolean {
    return this.store.get(SAVE_KEYS.main) !== (this.seen ?? null);
  }

  /** Takes the main under the key as the compare-and-swap baseline (a session that did not load it). */
  adopt(): void {
    this.seen = this.store.get(SAVE_KEYS.main);
  }

  /**
   * A current-format envelope around a fresh cold-open state: where the format has numbers (null repair,
   * M11). A nullable number field must be null in this template (none exists in plain objects today).
   */
  private templateEnvelope(): SaveEnvelope {
    if (this.template === null || this.template.defs !== this.o.defs) {
      this.template = {
        defs: this.o.defs,
        env: {
          v: SAVE_VERSION,
          game: SAVE_GAME,
          savedAtWallMs: 0,
          lastSeenWallMs: 0,
          maxWallSeenMs: 0,
          defsHash: '',
          sim: coldOpenState(this.o.defs, 0),
          meta: { settings: {}, sessionCount: 0, uiFlags: {} },
        },
      };
    }
    return this.template.env;
  }

  private logFixes(subject: 'load' | 'write', fixes: readonly SanitizeFix[]): void {
    if (fixes.length === 0) return;
    this.o.log({
      event: 'save_sanitized',
      subject,
      value: fixes.length,
      extra: describeFixes(fixes, this.o.sanitizeLogMax ?? fixes.length),
    });
  }

  /** Validates and prepares an envelope (a parsed object or its JSON text). */
  parse(raw: unknown): { ok: true; loaded: Loaded } | { ok: false; reason: string } {
    let obj: unknown = raw;
    try {
      // a private deep copy (migrations and repairs never touch the caller's object) that keeps NaN /
      // ±Infinity visible for the sanitizer (a plain JSON round trip would turn them into null)
      obj = typeof raw === 'string' ? JSON.parse(raw) : jsonCopyKeepingNonFinite(raw);
    } catch {
      return { ok: false, reason: 'not JSON' };
    }
    const fixes = sanitizeNonFinite(obj);
    const h = checkHeader(obj);
    if (!h.ok) return h;
    const m = migrate(h.header as unknown as RawEnvelope, this.o.migrations ?? MIGRATIONS);
    if (!m.ok) return m;
    fixes.push(...repairNulls(m.env, this.templateEnvelope()));
    const e = checkEnvelope(m.env);
    if (!e.ok) return e;
    const env = e.env as unknown as SaveEnvelope;
    const state = env.sim;
    try {
      canonicalJson(state); // integers only: a float or NaN anywhere is corruption
    } catch (err) {
      return { ok: false, reason: `sim: ${err instanceof Error ? err.message : String(err)}` };
    }
    stripTransient(state);
    let mismatch: ReconcileReport | null = null;
    if (env.defsHash !== this.o.defsHash) {
      try {
        mismatch = reconcileWithDefs(state, this.o.defs);
      } catch (err) {
        return {
          ok: false,
          reason: `defs mismatch: ${err instanceof Error ? err.message : String(err)}`,
        };
      }
    }
    // always, also after a reconcile: a state the sim would choke on is corrupt (→ .bak → fresh start)
    let bad: string[];
    try {
      bad = checkInvariants(state, this.o.defs);
    } catch (err) {
      bad = [err instanceof Error ? err.message : String(err)];
    }
    if (bad.length > 0)
      return {
        ok: false,
        reason: `sim invariants${mismatch !== null ? ' after the defs reconcile' : ''}: ${bad[0]}`,
      };
    this.logFixes('load', fixes);
    if (mismatch !== null)
      this.o.log({
        event: 'defs_mismatch',
        subject: `${env.defsHash}→${this.o.defsHash}`,
        value: mismatch.refund,
        extra: mismatch.dropped.map((x) => `${x.kind}:${x.what}`).join(';'),
      });
    return { ok: true, loaded: { state, meta: env.meta, env, mismatch } };
  }

  /** Loads the main save, falling back to .bak (the bad main is kept as .corrupt). */
  load(): LoadResult {
    const main = this.store.get(SAVE_KEYS.main);
    this.seen = main; // what stays under the key (a corrupt main too: it is kept there until the next save)
    let corrupt: string | null = null;
    if (main !== null) {
      const r = this.parse(main);
      if (r.ok) {
        this.mainText = main;
        this.mainSim = JSON.stringify(r.loaded.state);
        this.maxWall = r.loaded.env.maxWallSeenMs;
        return { k: 'loaded', from: 'main', corrupt: null, ...r.loaded };
      }
      corrupt = r.reason;
      this.store.set(SAVE_KEYS.corrupt, main);
      this.o.log({ event: 'save_corrupt', subject: 'main', value: main.length, extra: r.reason });
    }
    const bak = this.store.get(SAVE_KEYS.bak);
    if (bak !== null) {
      const r = this.parse(bak);
      if (r.ok) {
        this.maxWall = r.loaded.env.maxWallSeenMs;
        return { k: 'loaded', from: 'bak', corrupt, ...r.loaded };
      }
      this.o.log({ event: 'save_corrupt', subject: 'bak', value: bak.length, extra: r.reason });
      return { k: 'failed', reason: `main: ${corrupt ?? 'missing'}; bak: ${r.reason}` };
    }
    return corrupt === null ? { k: 'none' } : { k: 'failed', reason: `main: ${corrupt}; no bak` };
  }

  /** Writes a save of `state` (its transient fields are stripped on load, not here). */
  save(state: SimState, meta: SaveMeta): SaveWrite {
    if (this.disabled) return { ok: false, bytes: 0, blocked: true };
    const prev = this.store.get(SAVE_KEYS.main);
    if (this.seen === undefined) this.seen = prev;
    else if (prev !== this.seen) return { ok: false, bytes: 0, conflict: true };
    const now = this.o.now();
    if (now > this.maxWall) this.maxWall = now;
    const env: SaveEnvelope = {
      v: SAVE_VERSION,
      game: SAVE_GAME,
      savedAtWallMs: now,
      lastSeenWallMs: now,
      maxWallSeenMs: this.maxWall,
      defsHash: this.o.defsHash,
      sim: state,
      meta,
    };
    const fixes: SanitizeFix[] = [];
    const text = stringifySanitized(env, [meta.settings, meta.uiFlags], fixes);
    this.logFixes('write', fixes);
    const sim = JSON.stringify(state);
    // the current main becomes the .bak when the state moved on — and only a good one (a corrupt main must
    // never replace a good .bak) — and only after the new main is written: a failed write (quota, blocked
    // storage) keeps both the old main and the old .bak
    const rotate =
      prev !== null && sim !== this.mainSim && (prev === this.mainText || this.parse(prev).ok);
    const ok = this.store.set(SAVE_KEYS.main, text);
    if (!ok) return { ok, bytes: text.length };
    if (rotate && prev !== null) this.store.set(SAVE_KEYS.bak, prev);
    this.mainText = text;
    this.mainSim = sim;
    this.seen = text;
    return { ok, bytes: text.length };
  }

  /** Removes the save and its backup (the .corrupt copy is kept). Refused while disabled. */
  reset(): void {
    if (this.disabled) return;
    this.store.remove(SAVE_KEYS.main);
    this.store.remove(SAVE_KEYS.bak);
    this.mainText = null;
    this.mainSim = null;
    this.seen = null;
  }
}
